import React, { useState, useEffect } from "react";
import { MDBTooltip } from "mdb-react-ui-kit";
import TimeZones from "../../constants/TimeZones";
import { successMsg } from "../SuccessHandler";
import TransparentTextInput from "../TransparentTextInput";
import ErrorCodes from "../../constants/ErrorCodes";
import FilterColumns from "../utilities/FilterColumns";
import SiteConfig from "../../constants/SiteConfig";
import Select from "react-select";
import OptionsSelects from "../../constants/OptionsSelects";
import { useDeleteFunctions } from "../../functions";
import ConfirmDialog from "../utilities/ConfirmDialog";

const TrackListTable = ({
  trackList,
  setTrackList,
  gameProviders,
  trackCategoryCbx,
  filterInput,
  setFilterInput,
  selectedTrackType,
  setSelectedTrackType,
  selectedProvider,
  setSelectedProvider,
  data,
  divRef,
  setHasChanges,
  shouldFocusLastTrack,
  setShouldFocusLastTrack,
  tableRef,
  trackOptionSelected,
  setTrackOptionSelected,
  setTrackName,
  setChangeData,
  countries,
  allStates,
  setSessionNoLongerExists,
  columns,
  setColumns,
  publicIP,
  loadingTracks,
  filteredData,
  setFilteredData,
  systemPreferences,
}) => {
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [editingCountry, setEditingCountry] = useState(null);
  const [editingTimeZone, setEditingTimeZone] = useState(null);
  const [editingCategoryRow, setEditingCategoryRow] = useState(null);
  const [editingTrackType, setEditingTrackType] = useState(null);
  const [editingCloseMethod, setEditingCloseMethod] = useState(null);
  const [editingProvider, setEditingProvider] = useState(null);
  const [editingScoringRule, setEditingScoringRule] = useState(null);
  const [editingState, setEditingState] = useState(null);
  const { deleteTrack } = useDeleteFunctions();
  const [filteredOptions, setFilteredOptions] = useState([]);

  useEffect(() => {
    const value = systemPreferences.TrackTypesEnabled;

    const newOptions = OptionsSelects.trackTypeOptions.filter((opt) => {
      if (opt.value === 0) {
        // Thoroughbred
        return ![0, 2, 4, 6].includes(value) || value === 7;
      }
      if (opt.value === 1) {
        // Harness
        return ![0, 1, 4, 5].includes(value) || value === 7;
      }
      if (opt.value === 2) {
        // Greyhounds
        return ![0, 1, 2, 3].includes(value) || value === 7;
      }
      return true;
    });

    setFilteredOptions(newOptions);
  }, [systemPreferences.TrackTypesEnabled]);

  //Highlights the last track inserted in the track table
  useEffect(() => {
    if (shouldFocusLastTrack && tableRef.current) {
      if (tableRef.current) {
        const rows = tableRef.current.getElementsByTagName("tr");
        const secondLastRow = rows[rows.length - 2];
        const lastRow = rows[rows.length - 1];
        secondLastRow.style.backgroundColor = null;
        lastRow.style.backgroundColor = "#ccf5ff";
        lastRow.scrollIntoView();
      }
      const div = divRef.current;
      if (div) {
        div.scrollTop = div.scrollHeight;
      }

      setShouldFocusLastTrack(false);
    }
  }, [shouldFocusLastTrack]);

  //This useEffect filters the Tracks table according to Track Type, Track Code, Track Name and Provider.
  useEffect(() => {
    let filteredData = trackList;
    //Track Type combo box filter
    if (selectedTrackType) {
      filteredData = filteredData.filter((item) =>
        item.TrackType.toString().includes(selectedTrackType),
      );
    }

    //Provider combo box filter
    if (selectedProvider) {
      filteredData = filteredData.filter((item) =>
        item.IdGameProvider.toString().includes(selectedProvider),
      );
    }

    //Track Code and Track Name input filter
    if (filterInput) {
      filteredData = filteredData.filter(
        (item) =>
          item.TrackName.toLowerCase().includes(filterInput.toLowerCase()) ||
          item.TrackCode.toLowerCase().includes(filterInput.toLowerCase()),
      );
    }
    setFilteredData(filteredData);
  }, [trackList, selectedTrackType, selectedProvider, filterInput]);

  // This constant is used internally to set the selected track type to the filters
  const handleFilterTrackTypeChange = (event) => {
    setSelectedTrackType(event.target.value);
  };

  // This constant is used internally to set the selected provider to the filters
  const handleFilterProviderChange = (event) => {
    setSelectedProvider(event.target.value);
  };

  //This constant is used internally to set the selected Track Name or Track Code in the filters
  const handleInputFilterChange = (event) => {
    setFilterInput(event.target.value);
  };

  //Onchange Tracks Table
  const tracksTableChangeHandler = (e, value, keyOverride, fieldName) => {
    const x = [...filteredData];
    let name, key, val;

    if (e && e.target) {
      name = e.target.name;
      key = e.target.getAttribute("data-key");
      val = e.target.value;
    } else {
      name = fieldName;
      key = keyOverride;
      val = value;
    }

    if (
      name === "TrackCode" ||
      name === "ShortName" ||
      name === "AmtoteTrackCode"
    ) {
      val = val.toUpperCase();
    }

    if (name === "IdTrackCategory") {
      val = Number(val);
    }

    x[key][name] = val;
    x[key]["Change"] = true;
    setFilteredData(x);
    setTrackList(x);
    setHasChanges(true);
    setChangeData(true);
  };

  //Onchange Special Event Checkboxes
  const handleCheckboxSpecialEventChange = (id) => {
    setTrackList((prevState) => {
      const newData = prevState.map((item) => {
        if (item.TrackID === id) {
          return {
            ...item,
            SpecialEvent: !item.SpecialEvent,
            Change: true,
          };
        }
        return item;
      });
      return newData;
    });
    setHasChanges(true);
    setChangeData(true);
  };

  // Delete Tracks Function
  const handleDeleteTrack = (trackID, i, trackName) => {
    ConfirmDialog(
      `Do you want to delete the <strong>${trackName}</strong> Track?`,
    ).then(async (result) => {
      if (result.isConfirmed) {
        const res = await deleteTrack(
          {
            DeleteID: trackID,
            PhoneLine: SiteConfig.LOGIN_PHONE_LINE,
            IP: publicIP,
          },
          data,
        );

        if (res?.error) {
          if (
            res.Result === ErrorCodes.InvalidSession ||
            res.Result === ErrorCodes.SessionNoLongerExists
          ) {
            setSessionNoLongerExists(true);
          }

          return;
        }

        successMsg("Delete Track successfully");
        const updatedItems = [...filteredData];
        updatedItems.splice(i, 1);
        setFilteredData(updatedItems);
        setTrackOptionSelected("");
        setTrackName("");
      }
    });
  };

  const selectedRow = (trackId, trackName) => {
    setTrackOptionSelected(trackId);
    setTrackName(trackName);

    let tabla = document.getElementById("tracksTable");

    tabla.addEventListener("click", function (event) {
      const rows = tableRef.current.getElementsByTagName("tr");
      const lastRow = rows[rows.length - 1];
      let rowClicked = event.target.parentNode;

      if (lastRow.contains(rowClicked)) {
        lastRow.style.backgroundColor = null;
      }
    });
  };

  // Countries and States
  useEffect(() => {
    const options = countries.map((option) => ({
      value: option.CountryCode,
      label: option.CountryCode + ": " + option.CountryName,
    }));
    const finalOptions = [...options];

    setCountryOptions(finalOptions);
  }, [countries]);

  useEffect(() => {
    const options = allStates.map((state) => ({
      value: state.StateCode,
      label: state.StateCode + ": " + state.StateName,
      countryCode: state.CountryCode,
    }));
    const finalOptions = [...options];

    setStateOptions(finalOptions);
  }, [allStates]);

  //Onchange Enabled Checkboxes
  const handleCheckboxEnabledChange = (id) => {
    setTrackList((prevState) => {
      const newData = prevState.map((item) => {
        if (item.TrackID === id) {
          return {
            ...item,
            Enabled: !item.Enabled,
            Change: true,
          };
        }
        return item;
      });
      return newData;
    });
    setHasChanges(true);
    setChangeData(true);
  };

  //Charge Open Bets Table
  let headers,
    tableContent = [];

  if (trackList) {
    tableContent = filteredData.map((item, i) => {
      return (
        <tr
          key={i}
          onClick={() => selectedRow(item.TrackID, item.TrackName)}
          className={trackOptionSelected === item.TrackID ? "shadedRow" : ""}
        >
          <td className="d-none">{item.TrackID}</td>
          {/* Track Type */}
          <td
            style={{ verticalAlign: "middle", textAlign: "center" }}
            hidden={columns.find((column) => column.name === "Track Type")}
            onClick={() => {
              setEditingTrackType(item.TrackID);
              selectedRow(item.TrackID, item.TrackName);
            }}
            onFocus={() => {
              setEditingTrackType(item.TrackID);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setEditingTrackType(item.TrackID);
              }
            }}
          >
            {editingTrackType === item.TrackID ? (
              <Select
                menuPortalTarget={document.body}
                menuPosition="fixed"
                onClick={() => {
                  selectedRow(item.TrackID, item.TrackName);
                }}
                autoFocus
                onFocus={() => selectedRow(item.TrackID, item.TrackName)}
                value={filteredOptions.find(
                  (opt) => opt.value === item.TrackType,
                )}
                options={filteredOptions}
                onChange={(selectedOption) => {
                  tracksTableChangeHandler(
                    null,
                    selectedOption.value,
                    i,
                    "TrackType",
                  );
                  setEditingTrackType(null);
                }}
                onBlur={() => setEditingTrackType(null)}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 99999,
                    fontSize: 12,
                  }),
                  container: (provided) => ({
                    ...provided,
                    minWidth: 130,
                    fontSize: 12,
                  }),
                  control: (provided) => ({
                    ...provided,
                    fontSize: 12,
                  }),
                }}
              />
            ) : (
              <label
                tabIndex={0}
                style={{
                  fontSize: "12px",
                  width: "130px",
                  display: "inline-block",
                  cursor: "pointer",
                  color: "#495057",
                }}
                onClick={() => {
                  selectedRow(item.TrackID, item.TrackName);
                }}
              >
                {OptionsSelects.trackTypeOptions.find(
                  (opt) => opt.value === item.TrackType,
                )?.label || "-"}
              </label>
            )}
          </td>
          {/* Enabled */}
          <td
            style={{ verticalAlign: "middle", textAlign: "center" }}
            hidden={columns.find((column) => column.name === "Enabled")}
          >
            <input
              key={"Enabled" + i}
              type="checkbox"
              id="Enabled"
              name="Enabled"
              checked={item.Enabled}
              style={{ height: "15px" }}
              className="form-control"
              onChange={() => handleCheckboxEnabledChange(item.TrackID)}
            ></input>
          </td>
          {/* Track Code */}
          <td
            style={{ verticalAlign: "middle", textAlign: "center" }}
            hidden={columns.find((column) => column.name === "Track Code")}
          >
            <TransparentTextInput
              id="TrackCode"
              name="TrackCode"
              type="text"
              dataKey={i}
              onChange={(e) => tracksTableChangeHandler(e)}
              error={!item.TrackCode ? true : false}
              value={item.TrackCode}
              errorLabel="Track Code is required"
              fontSize="12px"
              maxLength={5}
            />
          </td>
          {/* Track Name */}
          <td
            style={{
              verticalAlign: "middle",
              textAlign: "center",
              width: "220px",
            }}
            hidden={columns.find((column) => column.name === "Track Name")}
          >
            <TransparentTextInput
              id="TrackName"
              name="TrackName"
              type="text"
              dataKey={i}
              onChange={(e) => tracksTableChangeHandler(e)}
              error={!item.TrackName ? true : false}
              value={item.TrackName}
              errorLabel="Track Name is required"
              fontSize="12px"
              width="220px"
              maxLength={50}
            />
          </td>
          {/* Short Name */}
          <td
            style={{ verticalAlign: "middle", textAlign: "center" }}
            hidden={columns.find((column) => column.name === "Short Name")}
          >
            <input
              tabIndex={0}
              className="form-control"
              type="text"
              name="ShortName"
              data-key={i}
              value={item.ShortName}
              style={{
                backgroundColor: "transparent",
                border: "none",
                textAlign: "center",
                fontSize: "12px",
                textTransform: "uppercase",
              }}
              onChange={(e) => tracksTableChangeHandler(e)}
              maxLength={6}
            />
          </td>
          {/* Track Category */}
          <td
            style={{ verticalAlign: "middle", textAlign: "center" }}
            hidden={columns.find((column) => column.name === "Category")}
            onClick={() => {
              setEditingCategoryRow(item.TrackID);
            }}
            onFocus={() => {
              setEditingCategoryRow(item.TrackID);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setEditingCategoryRow(item.TrackID);
              }
            }}
          >
            {editingCategoryRow === item.TrackID ? (
              <Select
                menuPortalTarget={document.body}
                menuPosition="fixed"
                autoFocus
                onFocus={() => selectedRow(item.TrackID, item.TrackName)}
                value={trackCategoryCbx
                  .map((cat) => ({
                    value: cat.IDTrackCategory,
                    label: cat.Description,
                  }))
                  .find((opt) => opt.value === item.IdTrackCategory)}
                options={trackCategoryCbx.map((cat) => ({
                  value: cat.IDTrackCategory,
                  label: cat.Description,
                }))}
                onChange={(selectedOption) => {
                  tracksTableChangeHandler(
                    null,
                    selectedOption.value,
                    i,
                    "IdTrackCategory",
                  );
                  setEditingCategoryRow(null);
                }}
                onBlur={() => setEditingCategoryRow(null)}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 99999,
                    fontSize: 12,
                  }),

                  container: (provided) => ({
                    ...provided,
                    minWidth: 130,
                    fontSize: 12,
                  }),
                  control: (provided) => ({
                    ...provided,
                    fontSize: 12,
                  }),
                }}
              />
            ) : (
              <label
                tabIndex={0}
                style={{
                  fontSize: "12px",
                  width: "120px",
                  cursor: "pointer",
                  color: "#495057",
                }}
              >
                {trackCategoryCbx.find(
                  (cat) =>
                    Number(cat.IDTrackCategory) ===
                    Number(item.IdTrackCategory),
                )?.Description || "-"}
              </label>
            )}
          </td>
          {/* Track Time Zone */}
          <td
            style={{ verticalAlign: "middle", textAlign: "center" }}
            hidden={columns.find((column) => column.name === "Time Zone")}
            onClick={() => {
              setEditingTimeZone(item.TrackID);
            }}
            onFocus={() => {
              setEditingTimeZone(item.TrackID);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setEditingTimeZone(item.TrackID);
              }
            }}
          >
            {editingTimeZone === item.TrackID ? (
              <Select
                menuPortalTarget={document.body}
                menuPosition="fixed"
                autoFocus
                onFocus={() => selectedRow(item.TrackID, item.TrackName)}
                value={TimeZones.map((tz) => ({ value: tz, label: tz })).find(
                  (opt) => opt.value === item.TrackTimeZone,
                )}
                options={TimeZones.map((tz) => ({ value: tz, label: tz }))}
                onChange={(selectedOption) => {
                  tracksTableChangeHandler(
                    null,
                    selectedOption.value,
                    i,
                    "TrackTimeZone",
                  );
                  setEditingTimeZone(null);
                }}
                onBlur={() => setEditingTimeZone(null)}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 99999,
                    fontSize: 12,
                  }),
                  container: (provided) => ({
                    ...provided,
                    minWidth: 167,
                  }),
                  control: (provided) => ({
                    ...provided,
                    fontSize: 12,
                  }),
                }}
              />
            ) : (
              <label
                tabIndex={0}
                style={{
                  fontSize: "12px",
                  width: "210px",
                  cursor: "pointer",
                  color: "#495057",
                }}
              >
                {item.TrackTimeZone || "-"}
              </label>
            )}
          </td>
          {/* Close Method */}
          <td
            style={{
              verticalAlign: "middle",
              textAlign: "center",
            }}
            hidden={columns.find((column) => column.name === "Close Method")}
            onClick={() => {
              setEditingCloseMethod(item.TrackID);
            }}
            onFocus={() => {
              setEditingCloseMethod(item.TrackID);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setEditingCloseMethod(item.TrackID);
              }
            }}
          >
            {editingCloseMethod === item.TrackID ? (
              <Select
                menuPortalTarget={document.body}
                menuPosition="fixed"
                autoFocus
                onFocus={() => selectedRow(item.TrackID, item.TrackName)}
                value={OptionsSelects.closeMethodOptions.find(
                  (opt) => opt.value === item.DefaultCloseMethod,
                )}
                options={OptionsSelects.closeMethodOptions}
                onChange={(selectedOption) => {
                  tracksTableChangeHandler(
                    null,
                    selectedOption.value,
                    i,
                    "DefaultCloseMethod",
                  );
                  setEditingCloseMethod(null);
                }}
                onBlur={() => setEditingCloseMethod(null)}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 99999,
                    fontSize: 12,
                  }),
                  container: (provided) => ({
                    ...provided,
                    minWidth: 130,
                    fontSize: 12,
                  }),
                  control: (provided) => ({
                    ...provided,
                    fontSize: 12,
                  }),
                }}
              />
            ) : (
              <label
                tabIndex={0}
                style={{
                  fontSize: "12px",
                  minWidth: "110px",
                  cursor: "pointer",
                  color: "#495057",
                }}
              >
                {OptionsSelects.closeMethodOptions.find(
                  (opt) => opt.value === item.DefaultCloseMethod,
                )?.label || "-"}
              </label>
            )}
          </td>
          {/* Past Post */}
          <td
            style={{ verticalAlign: "middle", textAlign: "center" }}
            hidden={columns.find((column) => column.name === "Past Post")}
          >
            <TransparentTextInput
              id="DefaultPostCutoff"
              name="DefaultPostCutoff"
              type="number"
              dataKey={i}
              onChange={(e) => tracksTableChangeHandler(e)}
              error={item.DefaultPostCutoff < 0 ? true : false}
              value={item.DefaultPostCutoff}
              errorLabel="Past Post cannot be negative"
              fontSize="12px"
            />
          </td>
          {/* Tote Track Code */}
          <td
            style={{ verticalAlign: "middle", textAlign: "center" }}
            hidden={columns.find((column) => column.name === "Tote Track Code")}
          >
            <input
              className="form-control"
              type="text"
              name="AmtoteTrackCode"
              data-key={i}
              value={item.AmtoteTrackCode}
              onChange={(e) => tracksTableChangeHandler(e)}
              style={{
                backgroundColor: "transparent",
                border: "none",
                textAlign: "center",
                fontSize: "12px",
                textTransform: "uppercase",
              }}
              maxLength={3}
            />
          </td>
          {/* Provider */}
          <td
            style={{ verticalAlign: "middle", textAlign: "center" }}
            hidden={columns.find((column) => column.name === "Provider")}
            onClick={() => {
              setEditingProvider(item.TrackID);
            }}
            onFocus={() => {
              setEditingProvider(item.TrackID);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setEditingProvider(item.TrackID);
              }
            }}
          >
            {editingProvider === item.TrackID ? (
              <Select
                menuPortalTarget={document.body}
                menuPosition="fixed"
                autoFocus
                onFocus={() => selectedRow(item.TrackID, item.TrackName)}
                value={gameProviders
                  .map((prov) => ({
                    value: prov.IDGameProvider,
                    label: prov.Description,
                  }))
                  .find((opt) => opt.value === item.IdGameProvider)}
                options={gameProviders.map((prov) => ({
                  value: prov.IDGameProvider,
                  label: prov.Description,
                }))}
                onChange={(selectedOption) => {
                  tracksTableChangeHandler(
                    null,
                    selectedOption.value,
                    i,
                    "IdGameProvider",
                  );
                  setEditingProvider(null);
                }}
                onBlur={() => setEditingProvider(null)}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 99999,
                    fontSize: 12,
                  }),
                  container: (provided) => ({
                    ...provided,
                    minWidth: 130,
                    fontSize: 12,
                  }),
                  control: (provided) => ({
                    ...provided,
                    fontSize: 12,
                  }),
                }}
              />
            ) : (
              <label
                tabIndex={0}
                style={{
                  fontSize: "12px",
                  width: "180px",
                  cursor: "pointer",
                  color: "#495057",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
                title={
                  gameProviders.find(
                    (provider) =>
                      Number(provider.IDGameProvider) ===
                      Number(item.IdGameProvider),
                  )?.Description || "-"
                }
              >
                {gameProviders.find(
                  (provider) =>
                    Number(provider.IDGameProvider) ===
                    Number(item.IdGameProvider),
                )?.Description || "-"}
              </label>
            )}
          </td>
          {/* Special Event */}
          <td
            style={{ verticalAlign: "middle", textAlign: "center" }}
            hidden={columns.find((column) => column.name === "Special Event")}
          >
            <input
              key={"SpecialEvent" + i}
              type="checkbox"
              id="SpecialEvent"
              name="SpecialEvent"
              checked={item.SpecialEvent}
              style={{ height: "15px" }}
              className="form-control"
              onChange={() => handleCheckboxSpecialEventChange(item.TrackID)}
            ></input>
          </td>
          {/* Scoring Rule */}
          <td
            style={{
              verticalAlign: "middle",
              textAlign: "center",
              whiteSpace: "nowrap",
            }}
            hidden={columns.find((column) => column.name === "Scoring Rule")}
            onClick={() => {
              setEditingScoringRule(item.TrackID);
            }}
            onFocus={() => {
              setEditingScoringRule(item.TrackID);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setEditingScoringRule(item.TrackID);
              }
            }}
          >
            {editingScoringRule === item.TrackID ? (
              <Select
                menuPortalTarget={document.body}
                menuPosition="fixed"
                autoFocus
                onFocus={() => selectedRow(item.TrackID, item.TrackName)}
                value={OptionsSelects.scoringRuleOptions.find(
                  (opt) => Number(opt.value) === Number(item.ScoringRule),
                )}
                options={OptionsSelects.scoringRuleOptions}
                onChange={(selectedOption) => {
                  tracksTableChangeHandler(
                    null,
                    selectedOption.value,
                    i,
                    "ScoringRule",
                  );
                  setEditingScoringRule(null);
                }}
                onBlur={() => setEditingScoringRule(null)}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 99999,
                    fontSize: 12,
                  }),
                  container: (provided) => ({
                    ...provided,
                    minWidth: 130,
                    fontSize: 12,
                  }),
                  control: (provided) => ({
                    ...provided,
                    fontSize: 12,
                  }),
                }}
              />
            ) : (
              <label
                tabIndex={0}
                style={{
                  fontSize: "12px",
                  width: "130px",
                  cursor: "pointer",
                  color: "#495057",
                }}
              >
                {OptionsSelects.scoringRuleOptions.find(
                  (opt) => Number(opt.value) === Number(item.ScoringRule),
                )?.label || "-"}
              </label>
            )}
          </td>
          {/* Country */}
          <td
            style={{ verticalAlign: "middle", textAlign: "center" }}
            hidden={columns.find((column) => column.name === "Country")}
            onClick={() => {
              setEditingCountry(item.TrackID);
            }}
            onFocus={() => {
              setEditingCountry(item.TrackID);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                setEditingCountry(item.TrackID);
              }
            }}
          >
            {editingCountry === item.TrackID ? (
              <Select
                menuPortalTarget={document.body}
                menuPosition="fixed"
                autoFocus
                onFocus={() => selectedRow(item.TrackID, item.TrackName)}
                value={countryOptions.find(
                  (opt) => opt.value === item.CountryCode,
                )}
                options={countryOptions}
                onChange={(selectedOption) => {
                  tracksTableChangeHandler(
                    null,
                    selectedOption.value,
                    i,
                    "CountryCode",
                  );

                  tracksTableChangeHandler(null, "", i, "StateCode");
                  setEditingCountry(null);
                }}
                onBlur={() => setEditingCountry(null)}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 99999,
                    fontSize: 12,
                  }),
                  container: (provided) => ({
                    ...provided,
                    minWidth: 130,
                    fontSize: 12,
                  }),
                  control: (provided) => ({
                    ...provided,
                    fontSize: 12,
                  }),
                }}
                placeholder="Select a Country"
              />
            ) : (
              <label
                tabIndex={0}
                style={{
                  fontSize: "12px",
                  width: "180px",
                  cursor: "pointer",
                  color: "#495057",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
              >
                {(() => {
                  const country = countryOptions.find(
                    (country) => country.value === item.CountryCode,
                  );
                  return country ? `${country.value} - ${country.label}` : "-";
                })()}
              </label>
            )}
          </td>
          {/* State */}
          <td
            style={{ verticalAlign: "middle", textAlign: "center" }}
            hidden={columns.find((column) => column.name === "State")}
          >
            {editingState === item.TrackID ? (
              <Select
                menuPortalTarget={document.body}
                menuPosition="fixed"
                autoFocus
                value={
                  stateOptions
                    .filter((state) => state.countryCode === item.CountryCode)
                    .find((opt) => opt.value === item.StateCode) || null
                }
                options={
                  item.CountryCode
                    ? stateOptions.filter(
                        (state) => state.countryCode === item.CountryCode,
                      )
                    : []
                }
                onChange={(selectedOption) => {
                  tracksTableChangeHandler(
                    null,
                    selectedOption.value,
                    i,
                    "StateCode",
                  );
                  setEditingState(null);
                }}
                onBlur={() => setEditingState(null)}
                styles={{
                  menuPortal: (base) => ({
                    ...base,
                    zIndex: 99999,
                    fontSize: 12,
                  }),
                  container: (provided) => ({
                    ...provided,
                    minWidth: 130,
                    fontSize: 12,
                  }),
                  control: (provided) => ({
                    ...provided,
                    fontSize: 12,
                  }),
                }}
                placeholder="Select a State"
              />
            ) : (
              <label
                tabIndex={0}
                onClick={() => setEditingState(item.TrackID)}
                style={{
                  fontSize: "12px",
                  width: "180px",
                  cursor: "pointer",
                  color: "#495057",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                }}
              >
                {(() => {
                  const state = stateOptions.find(
                    (opt) =>
                      opt.value === item.StateCode &&
                      opt.countryCode === item.CountryCode,
                  );
                  return state ? state.label : "-";
                })()}
              </label>
            )}
          </td>
          {/* Delete */}
          <td
            style={{
              textAlign: "center",
              verticalAlign: "middle",
            }}
          >
            <MDBTooltip tag="i" wrapperProps={{ href: "#" }} title="Delete">
              <i
                className="fas fa-trash-alt"
                style={{
                  color: "#ff0000",
                  fontSize: "20px",
                  cursor: "pointer",
                }}
                onClick={() =>
                  handleDeleteTrack(item.TrackID, i, item.TrackName)
                }
              ></i>
            </MDBTooltip>
          </td>
        </tr>
      );
    });
  }

  const handleClick = (name, id) => {
    if (!columns.find((column) => column.name === name)) {
      setColumns([...columns, { name }]);
    }
  };

  // Table headers tracks

  headers = (
    <tr className="text-center">
      <th hidden>TrackID</th>
      <th
        onClick={() => handleClick("Track Type")}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "Track Type")}
      >
        Track Type
      </th>
      <th
        onClick={() => handleClick("Enabled")}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "Enabled")}
      >
        Enabled
      </th>
      <th
        onClick={() => handleClick("Track Code")}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "Track Code")}
      >
        Track Code
      </th>
      <th
        style={{ width: "250px" }}
        onClick={() => handleClick("Track Name")}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "Track Name")}
      >
        Track Name
      </th>
      <th
        onClick={() => handleClick("Short Name")}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "Short Name")}
      >
        Short Name
      </th>
      <th
        onClick={() => handleClick("Category")}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "Category")}
      >
        Category
      </th>
      <th
        onClick={() => handleClick("Time Zone")}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "Time Zone")}
      >
        Time Zone
      </th>
      <th
        onClick={() => handleClick("Close Method")}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "Close Method")}
      >
        Close Method
      </th>
      <th
        onClick={() => handleClick("Past Post")}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "Past Post")}
      >
        Past Post
      </th>
      <th
        onClick={() => handleClick("Tote Track Code")}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "Tote Track Code")}
      >
        Tote Track Code
      </th>
      <th
        onClick={() => handleClick("Provider")}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "Provider")}
      >
        Provider
      </th>
      <th
        onClick={() => handleClick("Special Event", 12)}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "Special Event")}
      >
        Special Event
      </th>
      <th
        onClick={() => handleClick("Scoring Rule", 13)}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "Scoring Rule")}
      >
        Scoring Rule
      </th>
      <th
        onClick={() => handleClick("Country", 14)}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "Country")}
      >
        Country
      </th>
      <th
        onClick={() => handleClick("State", 15)}
        className="headerTrackList"
        hidden={columns.find((column) => column.name === "State")}
      >
        State
      </th>
      <th></th>
    </tr>
  );

  const handleToggleColumn = (column) => {
    const updatedColumns = columns.filter((col) => col.name !== column.name);
    setColumns(updatedColumns);
  };

  const handleToggleAllColumns = () => {
    setColumns([]);
  };

  return (
    <>
      <div className="form-group">
        <label className="">Filters: </label>
        <select
          id="IdGameProvider"
          name="IdGameProvider"
          className="form-control ml-2"
          value={selectedProvider}
          style={{ width: "14%", fontSize: "14px", display: "inline-block" }}
          onChange={handleFilterProviderChange}
        >
          <option value={""}>All Provider Filter</option>
          {gameProviders.map((provider, i) => {
            return (
              <option key={i} value={provider.IDGameProvider}>
                {provider.Description}
              </option>
            );
          })}
        </select>
        <select
          className="form-control ml-2"
          id="TrackType"
          name="TrackType"
          value={selectedTrackType}
          onChange={handleFilterTrackTypeChange}
          style={{ width: "14%", fontSize: "14px", display: "inline-block" }}
        >
          <option value={""}>All Track Type Filter</option>
          {filteredOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          value={filterInput}
          onChange={handleInputFilterChange}
          placeholder={"Track Code or Track Name Search..."}
          className="form-control ml-2 mb-2"
          style={{ width: "20%", display: "inline-block" }}
        />
        <div style={{ display: "inline-block", marginLeft: "1%" }}>
          <FilterColumns
            columns={columns}
            onToggle={handleToggleColumn}
            onToggleAll={handleToggleAllColumns}
          />
        </div>
      </div>
      {loadingTracks ? (
        <div
          className="row d-flex justify-content-center mt-5 mb-5"
          style={{ textAlign: "center" }}
        >
          <div className="spinner-grow text-secondary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <div className="spinner-grow text-info" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <div className="spinner-grow text-warning" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : tableContent.length > 0 ? (
        <div
          className="divTables"
          style={{ overflow: "auto", marginTop: "10px" }}
          ref={divRef}
        >
          <table
            className="table table-hover table-sm"
            ref={tableRef}
            id="tracksTable"
          >
            <thead
              style={{
                position: "sticky",
                top: "0",
                background: "white",
                zIndex: "9998",
              }}
            >
              {headers}
            </thead>
            <tbody className="table-sm">{tableContent}</tbody>
          </table>
        </div>
      ) : (
        <div
          className="row d-flex justify-content-center mt-5 mb-5"
          style={{ textAlign: "center" }}
        >
          <p style={{ fontSize: "16px", color: "#6c757d" }}>
            No data to display
          </p>
        </div>
      )}
    </>
  );
};

export default TrackListTable;
